import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SEO from "./SEO";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";

gsap.registerPlugin(ScrollTrigger);

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Message sent successfully!");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-8 py-5 bg-white border border-gray-100 rounded-2xl focus:border-secondary focus:ring-4 focus:ring-secondary/5 outline-none transition-all duration-300 font-medium text-text-primary placeholder:text-text-secondary/40 text-sm shadow-sm";

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, {
        y: 40, opacity: 0, duration: 1, ease: "power4.out", delay: 0.2, clearProps: "all",
      });
      gsap.from(leftRef.current, {
        y: 60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.4, clearProps: "all",
      });
      gsap.from(rightRef.current, {
        y: 60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.5, clearProps: "all",
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Contact Us"
        description={`Get in touch with ${import.meta.env.VITE_COMPANY_NAME}. We are here to help you with your loan application or any queries.`}
        keywords={`contact ${import.meta.env.VITE_COMPANY_NAME}, customer support, loan help, financial assistance India`}
      />
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-gradient-to-b from-white via-gray-50/50 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10">
        {/* Support Hub Hero */}
        <section className="w-full pt-44 pb-20 text-center px-6 relative overflow-hidden">
          <div ref={headerRef} className="relative z-10 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-all hover:scale-105">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">24/7 Support Hub</span>
            </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-10 text-text-primary">
              We're Here <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">to Help.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-2xl max-w-2xl mx-auto font-medium leading-relaxed">
              Have a question about your application or our products? <br className="hidden md:block"/> Our team is standing by to guide you.
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 px-6 lg:px-12 mb-24">

          {/* Contact Details Grid */}
          <div ref={leftRef} className="lg:col-span-5 flex flex-col gap-8">
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/5 rounded-bl-full -z-10" />
              <h2 className="text-3xl font-black text-text-primary tracking-tight mb-8">Reach Out Directly</h2>
              
              <div className="space-y-6">
                {[
                  { icon: <FaPhoneAlt />, label: "Call Us", value: import.meta.env.VITE_COMPANY_PHONE, href: `tel:${import.meta.env.VITE_COMPANY_PHONE}` },
                  { icon: <FaEnvelope />, label: "Email Support", value: import.meta.env.VITE_COMPANY_EMAIL, href: `mailto:${import.meta.env.VITE_COMPANY_EMAIL}` },
                  { icon: <FaMapMarkerAlt />, label: "Visit Office", value: import.meta.env.VITE_COMPANY_ADDRESS, href: undefined },
                  { icon: <FaClock />, label: "Working Hours", value: "Mon - Sat: 10:00 AM - 6:00 PM", href: undefined },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-6 group">
                    <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-xl transition-all group-hover:bg-primary group-hover:text-white">
                      {item.icon}
                    </div>
                    <div className="flex flex-col">
                      <div className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-text-primary font-black hover:text-secondary transition-colors text-base">{item.value}</a>
                      ) : (
                        <span className="text-text-primary font-black text-base leading-snug">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map Card */}
            <div className="rounded-[3rem] overflow-hidden border border-gray-100 shadow-xl h-[300px] grayscale hover:grayscale-0 transition-all duration-700">
              <iframe
                title={import.meta.env.VITE_COMPANY_NAME}
                src={import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL}
                width="100%"
                height="100%"
                className="border-0 block"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* Conversational Form */}
          <form
            ref={rightRef}
            onSubmit={handleSubmit}
            className="lg:col-span-7 bg-white p-10 md:p-16 rounded-[3rem] border border-gray-100 shadow-2xl flex flex-col gap-10 relative overflow-hidden"
          >
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-tl-full -z-10" />
            
            <div>
              <div className="text-[10px] font-black text-secondary uppercase tracking-[0.3em] mb-4">Start a Conversation</div>
              <h2 className="text-4xl font-black text-text-primary tracking-tighter leading-none mb-4">
                Tell us how we can <span className="text-secondary">support you.</span>
              </h2>
              <p className="text-text-secondary text-base font-medium">Average response time: &lt; 2 hours</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-4">Full Name</label>
                <input type="text" name="fullName" placeholder="John Doe" value={formData.fullName} onChange={handleChange} required className={inputClass} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-4">Email Address</label>
                <input type="email" name="email" placeholder="john@example.com" value={formData.email} onChange={handleChange} required className={inputClass} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-4">Phone Number</label>
                <input type="tel" name="phone" placeholder="+91 00000 00000" value={formData.phone} onChange={handleChange} required className={inputClass} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-4">Your Message</label>
                <textarea name="message" placeholder="I have a question about..." value={formData.message} onChange={handleChange} rows={4} className={inputClass + " resize-none"} />
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <input
                type="checkbox"
                id="consent"
                checked={formData.consent}
                onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                className="mt-1 h-5 w-5 rounded-lg border-2 border-primary text-primary focus:ring-primary cursor-pointer"
              />
              <label htmlFor="consent" className="text-xs text-text-secondary font-medium leading-relaxed cursor-pointer">
                I agree to the privacy policy and consent to receive communications from {import.meta.env.VITE_COMPANY_NAME}.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-primary text-white font-black text-lg rounded-2xl shadow-xl hover:bg-secondary transition-all duration-500 hover:-translate-y-1 active:scale-95 disabled:opacity-60"
            >
              {loading ? "Establishing Connection..." : "Send Message"}
            </button>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  )
};

export default ContactUs;
